/* eslint-disable camelcase */
import { DateTime } from 'luxon';

import { APIConfig } from 'src/config';
import logger from 'src/util/logger';

/**
 * Generates the payload to be passed to intercom.
 * Basic payload will be the current time and platform domain.
 * Will append the logged in userdetails  from the local storage(if available).
 * @returns {object} - payload to intercom
 */
export const getIntercomPayload = () => {
  const loggedInUser = localStorage.getItem('loggedInUser');
  const userDetails = loggedInUser ? JSON.parse(localStorage.getItem('loggedInUser')) : '';
  const basicPayload = {
    last_request_at: Math.floor(DateTime.local().toSeconds()),
    powertracer_platform_domain: window.location.hostname,
  };
  if (!userDetails) {
    return basicPayload;
  }
  return { ...userDetails, ...basicPayload };
};

/**
 * Concatenates the first and last name of the user.
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string} - username
 */
export const getUserName = (firstName, lastName) => `${firstName || ''} ${lastName || ''}`?.trim();

// A navigation usually sets two titles (the layout's, then the page's), which
// would send two Intercom updates. Each title change restarts this timer, so
// one update is sent once the title has settled. Intercom allows about 20
// updates per user per 30 minutes, so halving the calls keeps "Last seen"
// updating. 250 ms covers the gap between the two titles (a few ms) without a
// noticeable delay; the exact value is not critical.
const TITLE_UPDATE_DEBOUNCE_MS = 250;

let titleObserver;
let titleUpdateTimer;

/**
 * Stop pinging Intercom on page title changes.
 * @returns {void}
 */
const stopTitleObserver = () => {
  titleObserver?.disconnect();
  titleObserver = undefined;
  clearTimeout(titleUpdateTimer);
};

/**
 * Initiate and renders Intercom widget, identifying the logged in user (if any).
 * Updates/pings intercom with the latest info when the page title changes (ex: page navigation).
 * @param {string} intercomAppId
 * @returns {void}
 */
export const renderIntercom = (intercomAppId) => {
  // eslint-disable-next-line
  (function () { var w = window; var ic = w.Intercom; if (typeof ic === "function") { ic('reattach_activator'); ic('update', w.intercomSettings); } else { var d = document; var i = function () { i.c(arguments); }; i.q = []; i.c = function (args) { i.q.push(args); }; w.Intercom = i; var l = function () { var s = d.createElement('script'); s.type = 'text/javascript'; s.async = true; s.src = `https://widget.intercom.io/widget/${intercomAppId}`; var x = d.getElementsByTagName('script')[0]; x.parentNode.insertBefore(s, x); }; if (w.attachEvent) { w.attachEvent('onload', l); } else { w.addEventListener('load', l, false); } } })();

  window.Intercom('boot', {
    app_id: intercomAppId,
    ...getIntercomPayload(),
  });

  // React 19 hoists each page's <title> into <head> as a new element, so watch
  // the whole <head> and ping Intercom only when the document title changes.
  stopTitleObserver();
  let lastTitle = document.title;
  titleObserver = new MutationObserver(() => {
    if (document.title === lastTitle) {
      return;
    }
    lastTitle = document.title;
    clearTimeout(titleUpdateTimer);
    titleUpdateTimer = setTimeout(() => {
      window.Intercom('update', getIntercomPayload());
    }, TITLE_UPDATE_DEBOUNCE_MS);
  });
  titleObserver.observe(
    document.head,
    { subtree: true, characterData: true, childList: true },
  );
};

/**
 * After a successful login render intercom widget
 */
export const intercomHandler = () => {
  const { INTERCOM_APP_ID } = APIConfig();
  if (INTERCOM_APP_ID) {
    renderIntercom(INTERCOM_APP_ID);
  } else {
    logger.log('Could not load Intercom widget because app id is missing');
  }
};

/**
 * Whether this deployment uses Intercom as its support channel. Deployments
 * that run their own support chat leave INTERCOM_APP_ID unset, so callers must
 * offer a non-Intercom route to support when this is false.
 * @returns {boolean} - true when Intercom is the configured support channel
 */
export const isIntercomEnabled = () => Boolean(APIConfig().INTERCOM_APP_ID);

/**
 * Open the Intercom messenger. No-op when the widget has not booted.
 * @returns {void}
 */
export const showIntercom = () => {
  if (typeof window.Intercom === 'function') {
    window.Intercom('show');
  }
};

/**
 * Set logged in user details in local storage.
 * Logged in User details may contain email, id, first and last name.
 * @param {object} userAttributes
 * @returns {void}
 */
export const setLoggedInUserDetails = (userAttributes) => {
  if (!userAttributes) {
    return;
  }
  const { email, family_name: lastName, given_name: firstName } = userAttributes;
  const loggedInUser = {
    email,
    name: getUserName(firstName, lastName),
    user_id: userAttributes['custom:id'],
  };
  localStorage.setItem('loggedInUser', JSON.stringify(loggedInUser));
  intercomHandler();
};

/**
 * Close/shutdown Intercom when user logs out or when a session ends.
 * Clears user information from the local storage.
 * @returns {void}
 */
export const shutdownIntercom = () => {
  stopTitleObserver();
  if (window.Intercom) {
    window.Intercom('shutdown');
  }
  localStorage.removeItem('loggedInUser');
};
